import type { Vec2, FocusAimState } from '../types.js';
import type { ClientToWorld } from './mouse.js';
import {
  createTouchControlsDOM,
  showJoystick,
  moveJoystickNub,
  hideJoystick,
  setButtonPressed,
  type TouchControlsDOM,
} from './touch-controls.js';

export interface TouchSample {
  moveDir: Vec2;
  moveMagnitude: number;
  focusAim: FocusAimState;
  dashPressed: boolean;
  pausePressed: boolean;
  /** Rising edge of an aim-side tap: fires the manual weapon. */
  firePressed: boolean;
  /** Direction from screen center (the ship) to the tap point when a tap
   * fired this tick; null otherwise. */
  fireDir: Vec2 | null;
  /** True while any joystick/aim/button touch is currently held. */
  active: boolean;
}

/** Max nub travel from the joystick origin, in client pixels. */
const JOYSTICK_RADIUS_PX = 56;
/** Dead zone around the joystick origin before movement registers. */
const JOYSTICK_DEADZONE_PX = 6;
/** Press-and-release under this many ms on the move side = tap (fires). */
const TAP_MAX_MS = 260;
/** The floating joystick lives in the bottom 65% of the screen (thumb zone). */
const STICK_ZONE_TOP_FRACTION = 0.35;
/** Drag distance (px) for full stick magnitude. */
const JOY_RANGE_PX = 48;

function normalizeOrZero(x: number, y: number): Vec2 {
  const len = Math.hypot(x, y);
  if (len < 1e-8) return { x: 0, y: 0 };
  return { x: x / len, y: y / len };
}

/**
 * Mobile touch input adapter — floating joystick model.
 *
 * - Renders a DOM overlay only on touch-capable devices
 *   (`navigator.maxTouchPoints > 0`, or the first `touchstart` /
 *   touch `pointerdown`).
 * - Hold inside the bottom active zone (bottom 65% of the screen): a
 *   joystick anchors at the touch point and the DRAG vector steers the
 *   ship — direction + analog magnitude. Holding still = parked (turret
 *   mode is easy). The stick doubles as Focus Aim.
 * - Quick tap (release < TAP_MAX_MS): fires the manual turret at the
 *   tapped point.
 * - Dash is a TWO-THUMB gesture: while the first finger steers, a second
 *   thumb double-taps (two taps within 320 ms). Parked double-taps are
 *   just two shots — hold-ground play can't misfire into a dash.
 * - On-screen buttons: pause only (top-right), rising edge, >= 44px with
 *   `touch-action: none`.
 * - Multi-touch: the joystick pointer steers; a second pointer taps/double-
 *   taps; further pointers are ignored (pause tracks its own `pointerId`).
 *
 * Pointer events are used throughout so touch and mouse share the same
 * event path; non-touch pointers are ignored here (the `Mouse` adapter
 * owns those).
 */
export class TouchControls {
  private dom: TouchControlsDOM | null = null;
  private visible = true;

  // Floating joystick: a hold inside the bottom active zone anchors the
  // stick at the touch point; the drag vector steers (direction + analog
  // magnitude). Holding without dragging = dead still (turret mode is easy).
  private movePointerId: number | null = null;
  private joyBaseX = 0;
  private joyBaseY = 0;
  private joyCurX = 0;
  private joyCurY = 0;
  private lastAimDir: Vec2 = { x: 0, y: 1 };

  // Second finger: while the first steers, a second thumb can tap (fire)
  // or double-tap (dash — tracked on its own lastTapTapMs so the steer
  // finger's press timing can't accidentally arm a dash). Further pointers
  // are ignored.
  private tapPointerId: number | null = null;
  private tapDownMs = 0;
  private tapWasDouble = false;
  private lastTapTapMs = 0;

  private pausePointerId: number | null = null;
  private dashPressed = false;
  private pausePressed = false;
  private firePressed = false;
  /** Direction from screen center to the last tap. */
  private fireDir: Vec2 | null = null;
  /** Press start (tap-vs-hold): release before TAP_MAX_MS = tap. */
  private moveDownMs = 0;

  private usedThisFrame = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly clientToWorld?: ClientToWorld
  ) {
    // Prevent the browser from turning touches on the canvas into
    // scroll/zoom gestures.
    this.canvas.style.touchAction = 'none';

    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerUp);
    this.canvas.addEventListener('touchstart', this.onTouchStart, {
      passive: false,
    });

    // Lazily create the overlay on genuinely touch-capable devices.
    if (navigator.maxTouchPoints > 0) {
      this.ensureOverlay();
    } else {
      window.addEventListener('touchstart', this.onFirstTouch, { once: true });
    }
  }

  private onFirstTouch = (): void => {
    this.ensureOverlay();
  };

  private onTouchStart = (e: TouchEvent): void => {
    e.preventDefault();
    this.ensureOverlay();
  };

  private ensureOverlay(): void {
    if (this.dom) return;
    this.dom = createTouchControlsDOM();
    document.body.appendChild(this.dom.root);
    this.dom.root.style.display = this.visible ? 'block' : 'none';

    this.dom.pauseButton.addEventListener('pointerdown', this.onPauseDown);
    this.dom.pauseButton.addEventListener('pointerup', this.onPauseUp);
    this.dom.pauseButton.addEventListener('pointercancel', this.onPauseUp);
    // The haptic switch overlay sits on top of the pause button: it gets
    // the tap (native iOS tick) and must run the same handlers.
    this.dom.pauseSwitch.addEventListener('pointerdown', this.onPauseDown);
    this.dom.pauseSwitch.addEventListener('pointerup', this.onPauseUp);
    this.dom.pauseSwitch.addEventListener('pointercancel', this.onPauseUp);
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch') return;
    this.ensureOverlay();
    if (!this.visible) return;
    this.usedThisFrame = true;

    // Floating joystick: holds inside the bottom active zone steer (drag
    // vector = direction + analog magnitude); presses elsewhere are tap-only
    // (fire on quick release; second-finger double-tap while steering =
    // dash). Parked double-taps are just two shots — turret-mode play never
    // misfires into a dash.
    const inStickZone = e.clientY >= window.innerHeight * STICK_ZONE_TOP_FRACTION;
    if (this.movePointerId === null && inStickZone) {
      this.moveDownMs = e.timeStamp;
      this.movePointerId = e.pointerId;
      this.joyBaseX = e.clientX;
      this.joyBaseY = e.clientY;
      this.joyCurX = e.clientX;
      this.joyCurY = e.clientY;
      this.capturePointer(e.pointerId);
      if (this.dom) showJoystick(this.dom, e.clientX, e.clientY);
    } else if (this.tapPointerId === null) {
      // Tap path (outside the stick zone, or a second finger while
      // steering): quick release = fire at the point; a double-tap on the
      // second finger while steering = dash (motion is guaranteed).
      if (this.movePointerId !== null && e.timeStamp - this.lastTapTapMs < 320) {
        this.dashPressed = true;
        this.lastTapTapMs = 0;
        this.tapWasDouble = true;
      } else {
        this.lastTapTapMs = e.timeStamp;
        this.tapWasDouble = false;
      }
      this.tapDownMs = e.timeStamp;
      this.tapPointerId = e.pointerId;
      this.capturePointer(e.pointerId);
    }
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (e.pointerId === this.movePointerId) {
      this.usedThisFrame = true;
      // Joystick: the nub rides the drag (clamped); the ship steers by the
      // vector, not the finger's position.
      this.joyCurX = e.clientX;
      this.joyCurY = e.clientY;
      if (this.dom) {
        const dx = this.joyCurX - this.joyBaseX;
        const dy = this.joyCurY - this.joyBaseY;
        const dist = Math.hypot(dx, dy);
        const cap = dist > JOY_RANGE_PX ? JOY_RANGE_PX / dist : 1;
        moveJoystickNub(this.dom, dx * cap, dy * cap);
      }
    }
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (e.pointerId === this.movePointerId) {
      // Tap-vs-hold: a quick release is a TAP — fire the manual turret at
      // the tapped point; a hold keeps steering instead.
      if (e.timeStamp - this.moveDownMs < TAP_MAX_MS) {
        this.firePressed = true;
        this.fireDir = this.centerOffsetDir(e.clientX, e.clientY);
        this.usedThisFrame = true;
      }
      this.releaseMove();
    } else if (e.pointerId === this.tapPointerId) {
      if (e.timeStamp - this.tapDownMs < TAP_MAX_MS && !this.tapWasDouble) {
        this.firePressed = true;
        this.fireDir = this.centerOffsetDir(e.clientX, e.clientY);
        this.usedThisFrame = true;
      }
      this.tapWasDouble = false;
      this.tapPointerId = null;
    }
  };

  private onPauseDown = (e: PointerEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    if (!this.visible || this.pausePointerId !== null || !this.dom) return;
    this.pausePointerId = e.pointerId;
    this.pausePressed = true; // rising edge; consumed by sample()
    this.usedThisFrame = true;
    setButtonPressed(this.dom.pauseButton, true);
    this.captureOn(this.dom.pauseButton, e.pointerId);
  };

  private onPauseUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.pausePointerId || !this.dom) return;
    this.pausePointerId = null;
    setButtonPressed(this.dom.pauseButton, false);
  };

  private capturePointer(pointerId: number): void {
    this.captureOn(this.canvas, pointerId);
  }

  private captureOn(el: HTMLElement, pointerId: number): void {
    try {
      el.setPointerCapture(pointerId);
    } catch {
      // Pointer may already be gone (e.g. fast cancel); tracking still works
      // via the regular up/cancel events.
    }
  }

  private releaseMove(): void {
    this.movePointerId = null;
    if (this.dom) hideJoystick(this.dom);
  }

  /** Normalized offset from the canvas center; screen y flips to world y. */
  private centerOffsetDir(clientX: number, clientY: number): Vec2 {
    const rect = this.canvas.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    return normalizeOrZero(clientX - cx, -(clientY - cy));
  }

  /**
   * Build the touch sample for the current tick. `dashPressed`,
   * `pausePressed`, and `firePressed` are one-tick rising edges and are
   * cleared on read.
   */
  sample(_playerPos: Vec2, _tick: number): TouchSample {
    // Floating joystick: the drag vector from the anchor is the steer —
    // direction + analog magnitude (full speed at JOY_RANGE_PX of drag).
    let moveDir: Vec2 = { x: 0, y: 0 };
    let moveMagnitude = 0;
    if (this.movePointerId !== null) {
      const dx = this.joyCurX - this.joyBaseX;
      const dy = -(this.joyCurY - this.joyBaseY); // screen y down → world y up
      const dist = Math.hypot(dx, dy);
      if (dist >= JOYSTICK_DEADZONE_PX) {
        moveDir = normalizeOrZero(dx, dy);
        moveMagnitude = Math.min(1, dist / JOY_RANGE_PX);
      }
    }

    let focusAim: FocusAimState;
    if (this.movePointerId !== null) {
      // The steering drag doubles as Focus Aim (aim along the stick).
      if (moveDir.x !== 0 || moveDir.y !== 0) this.lastAimDir = moveDir;
      focusAim = { held: true, dir: this.lastAimDir };
    } else {
      focusAim = { held: false, dir: this.lastAimDir };
    }

    const out: TouchSample = {
      moveDir,
      moveMagnitude,
      focusAim,
      dashPressed: this.dashPressed,
      pausePressed: this.pausePressed,
      firePressed: this.firePressed,
      fireDir: this.fireDir,
      active:
        this.movePointerId !== null ||
        this.pausePointerId !== null,
    };
    this.dashPressed = false;
    this.pausePressed = false;
    this.firePressed = false;
    this.fireDir = null;
    return out;
  }

  /**
   * True if any touch input happened since the last `clearFrame()`, or a
   * joystick/aim touch is still held.
   */
  wasUsedThisFrame(): boolean {
    return (
      this.usedThisFrame ||
      this.movePointerId !== null ||
      this.tapPointerId !== null
    );
  }

  clearFrame(): void {
    this.usedThisFrame = false;
  }

  /**
   * Drop queued one-tick edges (dash/pause/fire). Called when the sim stops
   * sampling (pause menu, overlays) so stale presses can't leak into the
   * resumed run.
   */
  clearPressedEdges(): void {
    this.dashPressed = false;
    this.pausePressed = false;
    this.firePressed = false;
    this.fireDir = null;
  }

  /**
   * Show or hide the overlay (e.g. from a settings toggle). Hiding releases
   * any in-progress touches.
   */
  setVisible(visible: boolean): void {
    this.visible = visible;
    if (this.dom) {
      this.dom.root.style.display = visible ? 'block' : 'none';
    }
    if (!visible) {
      this.releaseMove();
      this.tapPointerId = null;
      this.pausePointerId = null;
    }
  }

  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    this.canvas.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchstart', this.onFirstTouch);
    if (this.dom) {
      this.dom.pauseButton.removeEventListener('pointerdown', this.onPauseDown);
      this.dom.pauseButton.removeEventListener('pointerup', this.onPauseUp);
      this.dom.pauseButton.removeEventListener('pointercancel', this.onPauseUp);
      this.dom.root.remove();
      this.dom = null;
    }
  }
}
